import React, { ReactNode } from 'react';
import { ErrorBoundary as ReactErrorBoundary } from 'react-error-boundary';
import {
  Box,
  Typography,
  Button,
  Paper,
  Stack,
  Alert,
  Divider,
  Chip,
  IconButton,
  Collapse,
  Container,
  Tooltip
} from '@mui/material';
import {
  ErrorOutline as ErrorOutlineIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandIcon,
  BugReport as BugIcon,
  Home as HomeIcon,
  ContentCopy as CopyIcon,
  DeleteSweep as ClearCacheIcon,
  Storage as StorageIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { Link } from 'react-router-dom';

// Import the site logo
import hopdriveLogo from '@assets/vectors/logo-banner-light.svg';

const ErrorContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'flex-start',
  minHeight: '100vh',
  maxHeight: '100vh',
  overflow: 'auto',
  padding: theme.spacing(3, 4, 3), // Reduced top/bottom padding
  textAlign: 'center',
  backgroundColor: theme.palette.background.default,
  position: 'relative',
  // Ensure smooth scrolling
  scrollBehavior: 'smooth',
  '&::before': {
    content: '""',
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundImage: `radial-gradient(circle at 20% 20%, ${theme.palette.error.light}08 0%, transparent 50%),
                      radial-gradient(circle at 80% 80%, ${theme.palette.error.light}05 0%, transparent 50%),
                      radial-gradient(circle at 40% 60%, ${theme.palette.error.light}03 0%, transparent 50%)`,
    pointerEvents: 'none',
    zIndex: 0,
  },
}));

const ErrorContent = styled(Box)(({ theme }) => ({
  maxWidth: 800,
  width: '100%',
  textAlign: 'center',
  position: 'relative',
  zIndex: 1,
  // Ensure content has proper spacing and doesn't get cut off
  paddingBottom: theme.spacing(2), // Reduced padding
  // Allow the content to grow naturally
  minHeight: 'min-content',
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  opacity: 0.8,
}));

const LogoImage = styled('img')({
  height: '48px',
  maxWidth: '200px',
  objectFit: 'contain',
});

const StyledErrorIcon = styled(ErrorOutlineIcon)(({ theme }) => ({
  fontSize: '3rem',
  color: theme.palette.error.main,
  marginBottom: theme.spacing(1),
}));

const ExpandButton = styled(IconButton)(({ theme }) => ({
  transform: 'rotate(0deg)',
  marginLeft: 'auto',
  transition: theme.transitions.create('transform', {
    duration: theme.transitions.duration.shortest,
  }),
  '&.expanded': {
    transform: 'rotate(180deg)',
  },
}));

interface LayoutErrorFallbackProps {
  error: Error;
  resetErrorBoundary: () => void;
}

function LayoutErrorFallback({ error, resetErrorBoundary }: LayoutErrorFallbackProps) {
  const [showDetails, setShowDetails] = React.useState(false);
  const [copySuccess, setCopySuccess] = React.useState(false);
  // Check if this is an Emotion/styled component error
  const isEmotionError =
    error.message?.includes('is not a function') &&
    (error.stack?.includes('emotion') || error.stack?.includes('styled'));

  const isChunkError = error.message?.includes('Loading chunk') || error.message?.includes('Loading CSS chunk');

  // Utility functions for error actions
  const copyErrorToClipboard = async () => {
    const errorReport = `
🚨 HopDrive Admin Portal Error Report

**Error Type:** ${getErrorType().toUpperCase()}
**Error Title:** ${getErrorTitle()}
**Time:** ${new Date().toLocaleString()}
**URL:** ${window.location.href}
**User Agent:** ${navigator.userAgent}

**Error Message:**
${error.message}

**Stack Trace:**
${error.stack}

**Recommended Action:** ${getErrorDescription()}
    `.trim();

    try {
      await navigator.clipboard.writeText(errorReport);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
      // Fallback: create a temporary text area
      const textArea = document.createElement('textarea');
      textArea.value = errorReport;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }
  };

  const clearLocalStorage = () => {
    try {
      localStorage.clear();
      sessionStorage.clear();
      alert('Browser storage cleared successfully. The page will now reload.');
      window.location.reload();
    } catch (err) {
      console.error('Failed to clear storage:', err);
      alert('Failed to clear browser storage. Please try clearing it manually through browser settings.');
    }
  };

  const clearCacheAndReload = async () => {
    try {
      // Clear Apollo cache first
      try {
        const { clearApolloCache } = await import('@utils/cacheUtils');
        clearApolloCache();
        console.log('✅ Apollo cache cleared');
      } catch (apolloError) {
        console.warn('⚠️ Failed to clear Apollo cache:', apolloError);
      }

      // Clear service worker cache if available
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        for (const registration of registrations) {
          await registration.unregister();
        }
      }

      // Clear browser cache if supported (limited support)
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map(name => caches.delete(name)));
      }

      alert('All caches cleared successfully. The page will now perform a hard reload.');
      // Hard reload to bypass cache
      window.location.reload();
    } catch (err) {
      console.error('Failed to clear cache:', err);
      alert('Cache clearing may not be fully supported. Performing a hard reload instead.');
      window.location.reload();
    }
  };

  const getErrorType = () => {
    if (isEmotionError) return 'styling';
    if (isChunkError) return 'loading';
    return 'application';
  };

  const getErrorTitle = () => {
    const errorType = getErrorType();
    switch (errorType) {
      case 'styling':
        return 'Style Loading Error';
      case 'loading':
        return 'Resource Loading Error';
      default:
        return 'Application Error';
    }
  };

  const getErrorDescription = () => {
    const errorType = getErrorType();
    switch (errorType) {
      case 'styling':
        return 'There was an issue loading the page styles. This usually resolves with a page refresh.';
      case 'loading':
        return 'Some page resources failed to load. This may be due to a network issue or a recent update.';
      default:
        return 'An unexpected error occurred while loading this page. Our team has been notified.';
    }
  };

  const handleRefresh = () => {
    if (isChunkError || isEmotionError) {
      // For chunk/styling errors, do a hard refresh to clear cache
      window.location.reload();
    } else {
      // For other errors, try the reset function first
      resetErrorBoundary();
    }
  };

  return (
    <ErrorContainer>
      <ErrorContent>
        <Stack spacing={2.5} alignItems='center' sx={{ width: '100%' }}>
          <StyledErrorIcon />

          <Stack spacing={1.5} alignItems='center'>
            <Typography
              variant='h4'
              color='error'
              sx={{
                fontWeight: 700,
                textAlign: 'center',
                fontSize: { xs: '1.75rem', sm: '2rem', md: '2.25rem' },
                mb: 0.5,
              }}
            >
              {getErrorTitle()}
            </Typography>
            <Typography
              variant='body1'
              color='text.secondary'
              sx={{
                textAlign: 'center',
                maxWidth: '500px',
                lineHeight: 1.5,
                fontWeight: 400,
                opacity: 0.9,
                fontSize: '1rem',
              }}
            >
              {getErrorDescription()}
            </Typography>
          </Stack>

          {/* Technical Details - Moved higher up */}
          <Box sx={{ width: '100%', maxWidth: 600 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                padding: 1.5,
                borderRadius: 2,
                transition: 'all 0.2s ease',
                border: '1px solid transparent',
                '&:hover': {
                  borderColor: 'divider',
                  backgroundColor: 'action.hover',
                },
              }}
              onClick={() => setShowDetails(!showDetails)}
            >
              <BugIcon sx={{ mr: 1, color: 'text.secondary', fontSize: '1.1rem' }} />
              <Typography variant='body2' color='text.secondary' sx={{ fontWeight: 500, flex: 1 }}>
                Technical Details
              </Typography>
              <ExpandButton className={showDetails ? 'expanded' : ''} size='small' sx={{ color: 'text.secondary' }}>
                <ExpandIcon />
              </ExpandButton>
            </Box>

            <Collapse in={showDetails}>
              <Alert
                severity='error'
                sx={{
                  mt: 1.5,
                  textAlign: 'left',
                  '& .MuiAlert-message': { width: '100%' },
                }}
              >
                <Typography variant='body2' component='div' sx={{ mb: 1 }}>
                  <strong>Error Message:</strong>
                </Typography>
                <Typography variant='body2' component='div' sx={{ mb: 1.5, fontFamily: 'monospace' }}>
                  {error.message}
                </Typography>

                {error.stack && (
                  <>
                    <Typography variant='body2' component='div' sx={{ mb: 1 }}>
                      <strong>Stack Trace:</strong>
                    </Typography>
                    <Typography
                      variant='caption'
                      component='pre'
                      sx={{
                        fontSize: '0.75rem',
                        whiteSpace: 'pre-wrap',
                        maxHeight: 250,
                        overflow: 'auto',
                        backgroundColor: 'rgba(0,0,0,0.08)',
                        padding: 1.5,
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'divider',
                        fontFamily: 'monospace',
                        lineHeight: 1.4,
                        // Add better scrollbar styling
                        '&::-webkit-scrollbar': {
                          width: '8px',
                        },
                        '&::-webkit-scrollbar-track': {
                          backgroundColor: 'rgba(0,0,0,0.1)',
                          borderRadius: '4px',
                        },
                        '&::-webkit-scrollbar-thumb': {
                          backgroundColor: 'rgba(0,0,0,0.3)',
                          borderRadius: '4px',
                          '&:hover': {
                            backgroundColor: 'rgba(0,0,0,0.4)',
                          },
                        },
                      }}
                    >
                      {error.stack}
                    </Typography>
                  </>
                )}
              </Alert>
            </Collapse>
          </Box>

          <Divider sx={{ width: '100%', my: 2, opacity: 0.5 }} />

          {/* Primary Actions */}
          <Stack direction='row' spacing={2} flexWrap='wrap' justifyContent='center' sx={{ mb: 1 }}>
            <Button
              variant='contained'
              color='primary'
              startIcon={<RefreshIcon />}
              onClick={handleRefresh}
              size='medium'
            >
              {isChunkError || isEmotionError ? 'Refresh Page' : 'Try Again'}
            </Button>
            <Button variant='outlined' startIcon={<HomeIcon />} component={Link} to='/' size='medium'>
              Go Home
            </Button>
          </Stack>

          {/* Utility Actions */}
          <Stack direction='row' spacing={1} flexWrap='wrap' justifyContent='center'>
            <Tooltip title={copySuccess ? 'Copied!' : 'Copy error details for Slack/bug report'}>
              <Button
                variant='outlined'
                size='small'
                startIcon={<CopyIcon />}
                onClick={copyErrorToClipboard}
                color={copySuccess ? 'success' : 'inherit'}
                sx={{
                  minWidth: 'auto',
                  transition: 'all 0.2s ease',
                  ...(copySuccess && {
                    backgroundColor: 'success.light',
                    borderColor: 'success.main',
                  }),
                }}
              >
                {copySuccess ? 'Copied!' : 'Copy Details'}
              </Button>
            </Tooltip>

            <Tooltip title='Clear browser storage (localStorage & sessionStorage)'>
              <Button
                variant='outlined'
                size='small'
                startIcon={<StorageIcon />}
                onClick={clearLocalStorage}
                color='warning'
              >
                Clear Storage
              </Button>
            </Tooltip>

            <Tooltip title='Clear browser cache and perform hard reload'>
              <Button
                variant='outlined'
                size='small'
                startIcon={<ClearCacheIcon />}
                onClick={clearCacheAndReload}
                color='error'
              >
                Clear Cache
              </Button>
            </Tooltip>
          </Stack>

          <Typography
            variant='caption'
            color='text.secondary'
            sx={{
              textAlign: 'center',
              fontStyle: 'italic',
              opacity: 0.7,
              mt: 1,
            }}
          >
            If this issue continues, please contact the HopDrive support team or try clearing your browser cache.
          </Typography>
        </Stack>
      </ErrorContent>
    </ErrorContainer>
  );
}

interface LayoutErrorBoundaryProps {
  children: ReactNode;
}

export default function LayoutErrorBoundary({ children }: LayoutErrorBoundaryProps) {
  return (
    <ReactErrorBoundary
      FallbackComponent={LayoutErrorFallback}
      onError={(error, errorInfo) => {
        console.error('LayoutErrorBoundary caught an error:', error, errorInfo);

        // Log to external service if available
        if (typeof window !== 'undefined' && (window as any).Sentry) {
          (window as any).Sentry.captureException(error, {
            contexts: {
              layoutErrorBoundary: {
                componentStack: errorInfo.componentStack,
              },
            },
            tags: {
              errorBoundary: 'layout',
            },
          });
        }
      }}
    >
      {children}
    </ReactErrorBoundary>
  );
}